/**
 * Hook for sandbox checklist management.
 *
 * Fetches checklist items with status and artifacts from the new
 * profile-driven checklist API, and provides mutations for run/skip/progress.
 *
 * @module features/sandboxes/hooks/useSandboxChecklist
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import type { ChecklistItemWithStatus } from '@chatsdet/types';

const checklistKey = (templateId: string) => ['sandbox-checklist', templateId];

export async function fetchChecklist(templateId: string): Promise<ChecklistItemWithStatus[]> {
  const res = await fetch(`${API_URL}/org/environments/templates/${templateId}/checklist`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to fetch checklist');
  const json = (await res.json()) as { ok: boolean; data: ChecklistItemWithStatus[] };
  if (!json.ok) throw new Error('Failed to fetch checklist');
  return json.data;
}

async function postChecklist<T>(url: string, fallbackError: string): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  });
  if (!res.ok) {
    throw await toApiError(res, fallbackError);
  }
  const json = (await res.json()) as { ok: boolean; data: T; error?: string };
  if (!json.ok) {
    throw new Error(json.error ?? fallbackError);
  }
  return json.data;
}

function runItem(
  templateId: string,
  itemId: string
): Promise<{ conversationId: string; itemId: string }> {
  return postChecklist(
    `${API_URL}/org/environments/templates/${templateId}/checklist/${itemId}/run`,
    'Failed to run item'
  );
}

function makeProgressRequest(
  templateId: string
): Promise<{ conversationId?: string; itemId: string | null }> {
  return postChecklist(
    `${API_URL}/org/environments/templates/${templateId}/checklist/progress`,
    'Failed to make progress'
  );
}

async function skipItem(templateId: string, itemId: string): Promise<void> {
  await postChecklist(
    `${API_URL}/org/environments/templates/${templateId}/checklist/${itemId}/skip`,
    'Failed to skip item'
  );
}

/**
 * Hook for managing the sandbox setup checklist.
 * Polls while any item is running (5s interval).
 */
export function useSandboxChecklist(templateId: string | undefined) {
  const queryClient = useQueryClient();
  const key = checklistKey(templateId!);

  const checklistQuery = useQuery({
    queryKey: key,
    queryFn: () => fetchChecklist(templateId!),
    enabled: !!templateId,
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasRunning = data?.some((item) => item.itemStatus.status === 'running');
      return hasRunning ? 5_000 : false;
    },
  });

  const runMutation = useMutation({
    mutationFn: ({ itemId }: { itemId: string }) => runItem(templateId!, itemId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const progressMutation = useMutation({
    mutationFn: () => makeProgressRequest(templateId!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const skipMutation = useMutation({
    mutationFn: ({ itemId }: { itemId: string }) => skipItem(templateId!, itemId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  return {
    items: checklistQuery.data ?? [],
    isLoading: checklistQuery.isLoading,
    runItem: (itemId: string) => runMutation.mutateAsync({ itemId }),
    isRunningItem: runMutation.isPending,
    makeProgress: () => progressMutation.mutateAsync(),
    isMakingProgress: progressMutation.isPending,
    skipItem: (itemId: string) => skipMutation.mutateAsync({ itemId }),
    isSkipping: skipMutation.isPending,
  };
}
